import type {
  Dataset,
  Site,
  SiteCreate,
  SiteUpdate,
  SiteUpdateResult,
} from "@/types";
import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from "./client";

/**
 * Endpoints `/api/v1/sites` — CRUD scoped par owner courant (compte JWT
 * prioritaire, fallback cookie session anonyme — cf. `backend/app/sites/router.py`).
 */

const BASE = "/api/v1/sites";

export function listSites(): Promise<Site[]> {
  return apiGet<Site[]>(BASE);
}

/**
 * S26 (ADR-010 §8.3) — liste les **jeux de données** (`datasets`) d'un site,
 * du plus récent au plus ancien (`created_at` desc), owner-scopé via le site.
 *
 * Un **jeu de données** = un upload de conso dédupliqué par fingerprint du
 * contenu d'entrée. Le front consomme cette liste pour (a) le **choix de
 * source** au wizard (`dataset_id` porté jusqu'à `POST /simulations` ;
 * pré-sélection mono-jeu) et (b) la **sémantique de suppression D5** (préavis
 * « dernier scénario → jeu supprimé » via `simulation_count`).
 *
 * Owner-scopé : `404 SITE_NOT_FOUND` si le site est inconnu ou appartient à un
 * autre owner (anti-énumération, jamais 403).
 */
export function getSiteDatasets(siteId: string): Promise<Dataset[]> {
  return apiGet<Dataset[]>(`${BASE}/${siteId}/datasets`);
}

/**
 * Mode dégradé V2 — **unique** point d'écriture de `dataset.tariff_period`
 * (`PATCH /api/v1/sites/{site_id}/datasets/{dataset_id}`). Posé au « Continuer »
 * du wizard (Step2) selon la courbe de conso type choisie, sur les deux voies
 * (fichier + Enedis). La réponse 200 est le `DatasetRead` à jour.
 *
 * Owner-scopé comme `getSiteDatasets`. Erreurs : 404 `SITE_NOT_FOUND`,
 * 404 `DATASET_NOT_FOUND`, 409 `DATASET_NOT_DEGRADED` (jeu non journalier),
 * 422 (enum invalide).
 */
export function updateDatasetTariffPeriod(
  siteId: string,
  datasetId: string,
  tariffPeriod: "base" | "hchp" | "tempo",
): Promise<Dataset> {
  return apiPatch<Dataset, { tariff_period: "base" | "hchp" | "tempo" }>(
    `${BASE}/${siteId}/datasets/${datasetId}`,
    { tariff_period: tariffPeriod },
  );
}

export function createSite(payload: SiteCreate): Promise<Site> {
  return apiPost<Site, SiteCreate>(BASE, payload);
}

/**
 * Met à jour un site (`PUT /api/v1/sites/{id}` — PATCH partiel côté backend).
 *
 * S12-6 : un changement de coordonnées (`latitude`/`longitude`) relance les
 * simulations `completed` du site (verbatim CdC §E1.5). La réponse est une
 * enveloppe `SiteUpdateResult { site, relaunched }` — `relaunched` est le
 * nombre de simulations relancées, lu par le wizard pour le toast « X
 * simulations relancées ».
 */
export function updateSite(
  id: string,
  payload: SiteUpdate,
): Promise<SiteUpdateResult> {
  return apiPut<SiteUpdateResult, SiteUpdate>(`${BASE}/${id}`, payload);
}

export function deleteSite(id: string): Promise<void> {
  return apiDelete<void>(`${BASE}/${id}`);
}

/**
 * Déclenche le **probe Enedis** d'un site (`POST /api/v1/sites/{id}/enedis/probe`,
 * S14-4, account-only). Corps de requête **vide** : le PRM est lu côté serveur.
 *
 * La réponse 200 est le `SiteRead` réévalué (même schéma enrichi que
 * `GET /sites/{id}` : `prm_validated` / `prm_validated_at` à jour). Le call-site
 * (`useEnedisProbe`) **remplace** l'entrée du cache react-query (`['sites',
 * 'list']` + détail) avec ce `Site` — pas de re-fetch de la liste (§ 4.bis).
 *
 * Verdict figé (§ 4.bis) : 200 = probe exécuté quel qu'en soit le résultat
 * (succès / échec consentement-PRM / **indéterminé** → corps inchangé) ; le
 * statut se lit dans le corps (`prm_validated`). Erreurs HTTP possibles :
 * - 400 `ENEDIS_PRM_NOT_SET` — pas de PRM posé sur le site.
 * - 403 `ENEDIS_REQUIRES_ACCOUNT` — appelant anonyme (A1).
 * - 404 `SITE_NOT_FOUND` — site d'un autre owner / inexistant.
 */
export function probeEnedis(siteId: string): Promise<Site> {
  return apiPost<Site, undefined>(`${BASE}/${siteId}/enedis/probe`, undefined);
}
